/**
 * A single chat row.
 *
 * Agent turns render as plain, full-width prose — no role label, no avatar,
 * no bubble — so the agent reads as the app's own voice (matching the static
 * welcome greeting and the legacy ``ChatWindowMessage`` styling). User turns
 * render left-aligned as an initials avatar + a light grey rounded bubble,
 * the way the design and the legacy renderer distinguish the human's words
 * from the agent's. There are no "AGENT"/"YOU" labels.
 *
 * Message prose renders at the VDS body2 variant (14/regular) — agent text
 * and reasoning through the ``Markdown`` chokepoint (which sets body2), user
 * text via a body2 ``Typography`` in the bubble — matching the RA chat design
 * (Figma 63:1877, typography/body2).
 */

import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import type { SxProps, Theme } from '@mui/material';
import { Alert, Avatar, Box, Button, ButtonBase, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import { useAuth } from '@verantos-dev/core-auth';
import { useCohortProfileQuery } from '@verantos-dev/data-access-cohorts';
import { borderRadius } from '@verantos-dev/shared-ui-components/tokens';
import { ASSET_STATUS } from '@verantos-dev/types-projects';

import { AssetCard } from './AssetCard';
import { AttachmentCard } from './AttachmentCard';
import { CheckpointCard } from './CheckpointCard';
import { ClarifyingQuestionCard } from './ClarifyingQuestionCard';
import { CONCEPT_GROUP_CONDENSE_THRESHOLD, ConceptGroupBatchCard } from './ConceptGroupBatchCard';
import { LibraryMatchCard } from './LibraryMatchCard';
import { Markdown } from './Markdown';
import { MemoryProposalCard } from './MemoryProposalCard';
import { MemorySavedNotification } from './MemorySavedNotification';
import { ProcessAnalysisPrompt } from './ProcessAnalysisPrompt';
import { ReviewResolvedCard } from './ReviewResolvedCard';
import { ToolCallGroupCard } from './ToolCallGroupCard';
import { VariableFamilyGroupCard } from './VariableFamilyGroupCard';
import { downloadUploadSource } from '../api/conceptGroupPreviewQueries';
import { useVariableDetailByRef } from '../api/useVariableDetailByName';
import { useAnalysisStatusLabel } from '../hooks/useAnalysisStatusLabel';
import { useBatchPrewarmConceptGroups } from '../hooks/useBatchPrewarmConceptGroups';
import { useConceptGroupStatusLabel } from '../hooks/useConceptGroupStatusLabel';
import type {
  EpiChatSendPayload,
  EpiCheckpoint,
  EpiCheckpointStage,
  EpiMessage,
  EpiMessagePart,
  EpiResourceRef,
  EpiToolCall,
  EpiVariableFamily,
} from '../types/epi-chat-types';
import { resourceCardRoute } from '../utils/resourceCardRoute';
import { getReviewItems, isReviewItemAnswered } from '../utils/reviewItems';
import { isReviewReplyText } from '../utils/reviewReply';

interface CheckpointCardProps {
  checkpoint: EpiCheckpoint;
  onAnswer: (answer: EpiChatSendPayload) => void;
  disabled?: boolean;
}

/**
 * Stage → card registry. The ``intent`` / ``cohort_plan`` / ``concept_group``
 * stages never reach this registry — they always resolve through the asset
 * workflow's ``IntentApprovePrompt`` branch below. Stages without an entry
 * here (bindings / draft / validation / judge) fall back to the generic
 * ``CheckpointCard``. Adding a new stage with its own UX means adding one
 * entry here — no conditionals in the renderer below.
 */
const CHECKPOINT_CARDS: Partial<
  Record<EpiCheckpointStage, React.ComponentType<CheckpointCardProps>>
> = {};

interface MessageBubbleProps {
  message: EpiMessage;
  onAnswerQuestion: (answer: EpiChatSendPayload) => void;
  /** Click handler for inline artifact-ref cards — opens the drawer for that artifact. */
  onSelectArtifact?: (artifactId: string) => void;
  /**
   * Click handler for inline resource-ref cards (OR-6089) — navigates the
   * page to the resource's BE-computed route. The caller is expected to
   * wire this through ``useChatPreservingNavigate`` (project-page) or
   * plain ``useNavigate`` (other surfaces); when omitted the resource
   * card renders without click-through.
   */
  onNavigateResource?: (route: string) => void;
  /**
   * OR-6504: opens a variable GROUP's details page (``/projects/:pid/
   * variable-group/:familyId``). Threaded from the project page, which owns the
   * project id and the navigator; omitted on surfaces without routing, where the
   * group card then renders without click-through.
   */
  onOpenVariableGroup?: (familyId: string) => void;
  /** Active chat session id; threaded through to memory-proposal cards
   *  so 'this session' confirms can scope correctly. */
  sessionId?: string | null;
  /**
   * True when this is the LAST message in the transcript (AV-14). The intent
   * checkpoint's inline Approve button renders only while its message is the
   * latest one: once the researcher approves (an optimistic user turn is
   * appended) or types their own redirect, a newer message exists, ``isLast``
   * flips false, and the Approve affordance disappears — approval is
   * superseded.
   */
  isLast?: boolean;
  disabled?: boolean;
  /**
   * OR-6455: overrides the user-turn avatar initials. The avatar
   * otherwise derives from `useAuth()` — correct for a live chat
   * (the viewer IS the sender), but wrong for the internal read-only
   * cross-owner viewer, where every "user" turn was sent by the
   * chat's OWNER, not whoever is currently looking at it. Omitted on
   * every other surface, which keeps the `useAuth()` default.
   */
  userAvatarInitials?: string;
}

/**
 * Render-group representation. Walks the raw parts list and bundles
 * runs of `tool_call` parts into a single `tool_group` so the transcript
 * shows one collapsible row instead of N separate tool rows. Other
 * part kinds (text, reasoning, checkpoint, …) pass through unchanged.
 *
 * OR-6486: a run of more than `CONCEPT_GROUP_CONDENSE_THRESHOLD` concept-group
 * `resource_ref` parts bundles the same way, into one `concept_group_batch`
 * rendered as a single card of rows. A workbook upload is the flow that
 * produces such a run — every draft lands on the same streaming turn — and one
 * card per sheet would bury the turn's prose under a column of cards. Shorter
 * runs pass through as individual `AssetCard`s, unchanged.
 *
 * The `memoryProposal` field on a tool_group surfaces a `propose_memory`
 * card beneath the group; `memorySaved` (one or more) surfaces an inline
 * "memory saved + undo" notification when the agent fired `save_memory`.
 * Both sit outside the tool log so the epi can act on them without
 * expanding the work; the silent-save UX needs the notification visible
 * so users stay aware of writes that bypass the confirm card.
 */
type RenderGroup =
  | { kind: 'part'; part: EpiMessagePart; index: number }
  | { kind: 'concept_group_batch'; indexKey: string; resourceRefs: EpiResourceRef[] }
  | {
      kind: 'tool_group';
      indexKey: string;
      toolCalls: EpiToolCall[];
      memoryProposal: { toolCall: EpiToolCall } | null;
      memorySaved: EpiToolCall[];
    };

const groupParts = (parts: EpiMessagePart[]): RenderGroup[] => {
  const groups: RenderGroup[] = [];
  let pending: EpiToolCall[] | null = null;
  let pendingStartIndex = -1;
  let pendingConceptGroups: { part: EpiMessagePart; index: number; ref: EpiResourceRef }[] | null =
    null;
  const flushConceptGroups = () => {
    const run = pendingConceptGroups;
    pendingConceptGroups = null;
    if (!run || run.length === 0) return;
    if (run.length > CONCEPT_GROUP_CONDENSE_THRESHOLD) {
      groups.push({
        kind: 'concept_group_batch',
        indexKey: `concept_group_batch-${run[0].index}`,
        resourceRefs: run.map(({ ref }) => ref),
      });
      return;
    }
    run.forEach(({ part, index }) => groups.push({ kind: 'part', part, index }));
  };
  const flushPending = () => {
    if (pending && pending.length > 0) {
      const memoryProposal = pending.find(
        (tc) => tc.toolName === 'propose_memory' && tc.status === 'complete'
      );
      const memorySaved = pending.filter(
        (tc) => tc.toolName === 'save_memory' && tc.status === 'complete'
      );
      groups.push({
        kind: 'tool_group',
        indexKey: `tool_group-${pendingStartIndex}`,
        toolCalls: pending,
        memoryProposal: memoryProposal ? { toolCall: memoryProposal } : null,
        memorySaved,
      });
    }
    pending = null;
    pendingStartIndex = -1;
  };
  parts.forEach((part, index) => {
    if (part.kind === 'tool_call') {
      flushConceptGroups();
      if (pending === null) {
        pending = [];
        pendingStartIndex = index;
      }
      pending.push(part.toolCall);
      return;
    }
    if (part.kind === 'resource_ref' && part.resourceRef.resourceKind === 'concept_group') {
      flushPending();
      (pendingConceptGroups ??= []).push({ part, index, ref: part.resourceRef });
      return;
    }
    flushPending();
    flushConceptGroups();
    groups.push({ kind: 'part', part, index });
  });
  flushPending();
  flushConceptGroups();
  return groups;
};

export const MessageBubble = ({
  message,
  onAnswerQuestion,
  onSelectArtifact,
  onNavigateResource,
  onOpenVariableGroup,
  sessionId,
  isLast,
  disabled,
  userAvatarInitials,
}: MessageBubbleProps) => {
  const isUser = message.role === 'user';
  const { user } = useAuth();
  // Pinned clarify: unanswered clarifying questions keep only their prose in
  // the stream — the options render pinned above the composer (the layouts own
  // that panel). TODO: cleanup with research_assistant_pinned_clarify ff
  const pinnedClarifyEnabled = Boolean(useFeatureFlagSplit('research_assistant_pinned_clarify'));
  // Review card: this message's questions and library matches live in the card
  // at the end of the chat, so the stream keeps only their prose and, once
  // answered, a summary. TODO: cleanup with research_assistant_review_card ff
  const reviewCardEnabled = Boolean(useFeatureFlagSplit('research_assistant_review_card'));
  const reviewItems = reviewCardEnabled ? getReviewItems(message) : [];
  const reviewGroupAnswered = reviewItems.length > 0 && isReviewItemAnswered(reviewItems[0]);
  // An answered group stays in the chat as one summary card, at the position of
  // its first item; the rest of its items fold into it.
  const renderResolvedReview = (itemId: string, index: number) =>
    reviewItems[0].id === itemId ? (
      <Box key={index} sx={{ marginTop: 1 }}>
        <ReviewResolvedCard items={reviewItems} />
      </Box>
    ) : null;
  // A full-reload replay re-emits durable `ui#` events verbatim
  // (reload/live parity), so an analysis resource card created while this
  // flag was on for some session replays even after it's off for the
  // current one. Gate the render itself so a flag-off viewer never sees it,
  // regardless of whether the card arrived live or via replay.
  // TODO: cleanup with descriptive_analysis_impl ff
  const descriptiveAnalysisImplEnabled = Boolean(
    useFeatureFlagSplit('descriptive_analysis_impl', false)
  );

  // Initials for the user avatar — same source the legacy chat window used
  // (core-auth derives them from first/last name), UNLESS the caller passed
  // an explicit override (OR-6455: the read-only cross-owner viewer, where
  // the turn's actual sender is the chat's owner, not the logged-in
  // viewer). Empty when unavailable (e.g. unauthenticated test render);
  // the avatar then shows a blank circle.
  const userInitials = userAvatarInitials ?? user?.userInitials ?? '';

  // OR-6858: every concept-group resource-ref card in this message otherwise
  // fires its own independent by-id request (`useConceptGroupStatusLabel`
  // below). Collect the ids this message will resolve and batch them into one
  // request — a message's refs all belong to one project in practice, so the
  // list is conservatively limited to refs matching the FIRST ref's project,
  // and any outlier just falls back to its own card's independent fetch
  // untouched.
  const conceptGroupRefs = message.parts.filter(
    (part): part is Extract<EpiMessagePart, { kind: 'resource_ref' }> =>
      part.kind === 'resource_ref' && part.resourceRef.resourceKind === 'concept_group'
  );
  const conceptGroupBatchProjectId = conceptGroupRefs[0]?.resourceRef.projectId;
  const conceptGroupIdsToPrewarm = conceptGroupRefs
    .filter((part) => part.resourceRef.projectId === conceptGroupBatchProjectId)
    .map((part) => part.resourceRef.hintArgs?.conceptGroupId)
    .filter((id): id is string => typeof id === 'string');
  const deferConceptGroupFetch = useBatchPrewarmConceptGroups(
    conceptGroupIdsToPrewarm,
    conceptGroupBatchProjectId ?? undefined
  );

  const content = groupParts(message.parts).map((group) => {
    if (group.kind === 'concept_group_batch') {
      return (
        <ConceptGroupBatchCard
          key={group.indexKey}
          resourceRefs={group.resourceRefs}
          onNavigateResource={onNavigateResource}
          deferStatusFetch={deferConceptGroupFetch}
        />
      );
    }
    if (group.kind === 'tool_group') {
      return (
        <Box key={group.indexKey}>
          <ToolCallGroupCard toolCalls={group.toolCalls} />
          {group.memoryProposal && (
            <Box sx={{ marginTop: 0.5 }}>
              <MemoryProposalCard
                proposalResult={group.memoryProposal.toolCall.result}
                sessionId={sessionId}
                disabled={disabled}
              />
            </Box>
          )}
          {group.memorySaved.map((toolCall) => (
            <Box key={toolCall.toolId} sx={{ marginTop: 0.5 }}>
              <MemorySavedNotification
                savedResult={toolCall.result}
                sessionId={sessionId}
                disabled={disabled}
              />
            </Box>
          ))}
        </Box>
      );
    }
    const { part, index } = group;
    if (part.kind === 'text') {
      if (!part.text || !part.text.trim()) {
        return null;
      }
      if (isUser) {
        // The Review card's combined reply repeats the agent's questions, which are
        // Markdown, so its bubble renders them (and only that bubble: what the
        // researcher types themselves stays literal). TODO: cleanup with research_assistant_review_card ff
        if (reviewCardEnabled && isReviewReplyText(part.text)) {
          return (
            <Box key={index}>
              <Markdown dense sxProps={REVIEW_REPLY_MARKDOWN_SX}>
                {part.text}
              </Markdown>
            </Box>
          );
        }
        return (
          <Typography key={index} variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {part.text}
          </Typography>
        );
      }
      return (
        <Box key={index} sx={{ marginY: index > 0 ? 0.75 : 0 }}>
          <Markdown>{part.text}</Markdown>
        </Box>
      );
    }
    if (part.kind === 'reasoning') {
      // Orchestrator-level reasoning prose. Sub-agent reasoning now
      // routes to its parent tool's internalLog, never here. Render
      // through the same Markdown chokepoint as text parts so
      // structure is preserved; mute the color to keep it visually
      // subordinate without forcing italic on tables/code.
      if (!part.text.trim()) return null;
      return (
        <Box
          key={index}
          sx={{
            marginY: index > 0 ? 0.75 : 0,
            color: 'text.secondary',
          }}
        >
          <Markdown>{part.text}</Markdown>
        </Box>
      );
    }
    if (part.kind === 'clarifying_question') {
      // Once answered, the question keeps ONLY its prose, rendered by the same
      // branch that posed it so nothing shifts or restyles on answer. The
      // researcher's choice is already a normal user message right below
      // (``userMessageSent`` / ``userMessageEchoed`` push it).
      //
      // Pinned clarify: while unanswered AND on the transcript's last message
      // (the only position the layouts pin — mirror getPendingClarifyingQuestion),
      // the option list renders pinned above the composer and the stream keeps
      // only the agent's question prose. An unanswered question on an OLDER
      // message is superseded and never pinned, so it keeps the full in-stream
      // card — suppressing it too would strand its options with no surface at
      // all. TODO: cleanup with research_assistant_pinned_clarify ff
      const answeredProseOnly = part.question.answered;
      const pinnedProseOnly = pinnedClarifyEnabled && !part.question.answered && isLast;
      // Covered by the Review card: the summary card once its group is answered,
      // only the prose while it is pending on the last message. A superseded one
      // keeps its full card.
      const reviewQuestion = reviewItems.find(
        (item) => item.kind === 'question' && item.id === part.question.questionId
      );
      if (reviewQuestion && reviewGroupAnswered) {
        return renderResolvedReview(reviewQuestion.id, index);
      }
      const reviewProseOnly = Boolean(reviewQuestion) && isLast;
      if (answeredProseOnly || pinnedProseOnly || reviewProseOnly) {
        return (
          <Box key={index} sx={{ marginTop: 1 }}>
            <Markdown>{part.question.question}</Markdown>
          </Box>
        );
      }
      return (
        <Box key={index} sx={{ marginTop: 1 }}>
          <ClarifyingQuestionCard
            question={part.question}
            onAnswer={onAnswerQuestion}
            disabled={disabled}
          />
        </Box>
      );
    }
    if (part.kind === 'checkpoint' && part.checkpoint.stage === 'process_analysis') {
      // Replayed checkpoints re-render even when the flag is now off, so gate the
      // render itself (same reasoning as the analysis resource card below).
      // TODO: cleanup with descriptive_analysis_impl ff
      if (!descriptiveAnalysisImplEnabled) return null;
      const analysisCard = message.parts.find(
        (p): p is Extract<EpiMessagePart, { kind: 'resource_ref' }> =>
          p.kind === 'resource_ref' && p.resourceRef.resourceKind === 'analysis'
      );
      return (
        <Box key={index} sx={{ marginTop: 1 }}>
          <ProcessAnalysisPrompt
            checkpoint={part.checkpoint}
            onAnswer={onAnswerQuestion}
            assetName={part.checkpoint.assetDisplayName ?? analysisCard?.resourceRef.name ?? null}
            showProcess={Boolean(isLast) && !part.checkpoint.answered}
            disabled={disabled}
          />
        </Box>
      );
    }
    if (part.kind === 'checkpoint') {
      // OR-6290 (AV-14) + OR-6292 (cohort plan): the gate checkpoints don't
      // render a rich inline review card — the review lives on the asset's own
      // page (reached via the sibling ``resource_ref`` card). The checkpoint
      // part is STILL in the stream (it is the approval gate), so we render a
      // lean Approve prompt in its place: the researcher approves via the
      // checkpoint-approval message, or types a change into chat (no Edit
      // affordance). The gate (``draft_gating`` / ``require_approval``) is
      // backend + UI-independent — this only swaps the review surface, never
      // the approval mechanism.
      if (
        part.checkpoint.stage === 'intent' ||
        part.checkpoint.stage === 'cohort_plan' ||
        // OR-6271: the concept-group create-consent gate — a short yes/no before
        // creating a new group. Without this the Approve prompt never rendered
        // for the stage, so the gate was un-approvable (the "approval guard
        // failing" symptom).
        part.checkpoint.stage === 'concept_group'
      ) {
        // OR-6336: the deterministic review prompt (named after the sibling
        // asset card when one rendered) is UNCONDITIONAL. It was originally
        // suppressed when the message carried prose of its own, on the theory
        // that the agent's check-in text already asked for review — but a
        // well-behaved turn always opens with acknowledgment prose, and the
        // gate ends the model's turn right after the ``checkpoint`` tool call,
        // so its narration ("Now I'll create the draft…") almost never
        // contains the review ask. Gating on prose therefore left a bare
        // Approve button in virtually every real turn.
        const isCohortPlan = part.checkpoint.stage === 'cohort_plan';
        const isConceptGroup = part.checkpoint.stage === 'concept_group';
        const approveAssetKind: ApprovePromptAssetKind = isCohortPlan
          ? 'cohort'
          : isConceptGroup
          ? 'concept_group'
          : 'analytical_variable';
        // OR-6357 (issues 6+8): the slim request_approval gate carries the
        // gated asset's display name on the checkpoint itself — authoritative,
        // and correct even when a single turn touches multiple variables. The
        // sibling-card lookup below is the fallback for pre-OR-6357 payloads
        // on replay; its first-of-kind match is what rendered the WRONG name
        // in Uma's multi-variable turn (OR-6357 comment).
        // OR-6271: a concept_group consent gate has NO sibling asset card (it
        // fires BEFORE creation), so its copy comes from the checkpoint summary.
        const assetCard = isConceptGroup
          ? undefined
          : message.parts.find(
              (p): p is Extract<EpiMessagePart, { kind: 'resource_ref' }> =>
                p.kind === 'resource_ref' &&
                p.resourceRef.resourceKind === (isCohortPlan ? 'cohort' : 'analytical_variable')
            );
        return (
          <Box key={index} sx={{ marginTop: 1 }}>
            <IntentApprovePrompt
              checkpoint={part.checkpoint}
              assetKind={approveAssetKind}
              onAnswer={onAnswerQuestion}
              assetName={part.checkpoint.assetDisplayName ?? assetCard?.resourceRef.name ?? null}
              // The Approve button shows ONLY while this checkpoint is the
              // latest message and unanswered. Approving appends an optimistic
              // user turn (and marks the checkpoint answered), so the button
              // disappears the instant the approval — or any typed redirect —
              // supersedes the pending gate.
              showApprove={Boolean(isLast) && !part.checkpoint.answered}
              disabled={disabled}
              onOpenVariableGroup={onOpenVariableGroup}
            />
          </Box>
        );
      }
      // Stage → card registry. Adding a new checkpoint stage with
      // its own UX means adding an entry here, NOT a new
      // conditional. Stages without a registered card fall back to
      // the generic ``CheckpointCard``.
      const Card = CHECKPOINT_CARDS[part.checkpoint.stage] ?? CheckpointCard;
      return (
        <Box key={index} sx={{ marginTop: 1 }}>
          <Card checkpoint={part.checkpoint} onAnswer={onAnswerQuestion} disabled={disabled} />
        </Box>
      );
    }
    if (part.kind === 'av_selection_checkpoint') {
      const reviewMatch = reviewItems.find(
        (item) => item.kind === 'match' && item.id === part.checkpoint.checkpointId
      );
      if (reviewMatch) {
        if (reviewGroupAnswered) return renderResolvedReview(reviewMatch.id, index);
        // Pending on the last message: shown in the Review card at the end of the chat.
        if (isLast) return null;
      }
      return (
        <Box key={index} sx={{ marginTop: 1 }}>
          <LibraryMatchCard
            checkpoint={part.checkpoint}
            onAnswer={onAnswerQuestion}
            disabled={disabled}
          />
        </Box>
      );
    }
    if (part.kind === 'artifact_ref') {
      const ref = part.artifactRef;
      // OR-6345 backstop: an analytical variable's canonical chat card is the
      // new-style ``resource_ref`` (AssetCard). The legacy ``artifact_ref`` card
      // is redundant for AVs, and if a dedup race in the reducer ever lets one
      // slip through it would render the pre-redesign look next to (or instead
      // of) the new card. Suppress it here for AV kinds so the old-style card
      // can never surface. Compositions keep their ``artifact_ref`` — it IS
      // their canonical chip (no ``resource_ref`` sibling, per session_replay).
      if (ref.kind === 'av' || ref.kind === 'av_denovo') {
        return null;
      }
      const kindLabel = ref.kind ? ref.kind.replace('_', ' ').toUpperCase() : 'ARTIFACT';
      return (
        <ResourceCardLayout
          key={index}
          testId={`artifact-ref-${ref.artifactId}`}
          ariaLabel={`Open ${ref.dataElement} in artifact drawer`}
          kindLabel={kindLabel}
          name={ref.dataElement}
          metaText={metaLine([ref.name, formatCreatedAt(ref.createdAt)])}
          ctaLabel="Open"
          onClick={() => onSelectArtifact?.(ref.artifactId)}
        />
      );
    }
    if (part.kind === 'resource_ref') {
      // OR-6089: clickable card for a project resource the agent
      // is drafting or has mutated. Routes (via the host-injected
      // ``onNavigateResource``) to the TanStack-driven detail
      // page, where ``RESOURCE_UPDATED`` events drive in-place
      // refetches.
      //
      // Click handling:
      //   - ``planning``: clickable — routes to the pre-manifest
      //     skeleton page (existing ``ProjectVariableDraftPage``
      //     surface), which renders "Setting up…" copy until
      //     the live artifact lands. Gives the user a stable nav
      //     handle the moment the agent picks up the analytical variable task,
      //     before any drafter sub-agent runs.
      //   - ``drafting``: clickable — same skeleton target until
      //     the manifest commits and the route ratchets to the
      //     canonical detail page.
      //   - ``updating`` / ``ready``: clickable, routes to the
      //     live detail page.
      //   - ``error``: disabled — nothing committed server-side
      //     to land on.
      const ref = part.resourceRef;
      // OR-6230: every resource kind derives its status pill from the asset's
      // live backend ``assetStatus`` via its own card below.
      if (ref.resourceKind === 'cohort') {
        return (
          <CohortResourceRefCard
            key={index}
            resourceRef={ref}
            onNavigateResource={onNavigateResource}
          />
        );
      }
      if (ref.resourceKind === 'analytical_variable') {
        return (
          <AvResourceRefCard
            key={index}
            resourceRef={ref}
            onNavigateResource={onNavigateResource}
            chatId={sessionId}
          />
        );
      }
      if (ref.resourceKind === 'concept_group') {
        return (
          <ConceptGroupResourceRefCard
            key={index}
            resourceRef={ref}
            onNavigateResource={onNavigateResource}
            deferStatusFetch={deferConceptGroupFetch}
          />
        );
      }
      if (ref.resourceKind === 'analysis') {
        // TODO: cleanup with descriptive_analysis_impl ff
        if (!descriptiveAnalysisImplEnabled) return null;
        return (
          <AnalysisResourceRefCard
            key={index}
            resourceRef={ref}
            onNavigateResource={onNavigateResource}
          />
        );
      }
      // ``EpiResourceKind`` is exactly the four kinds above and the wire
      // schema rejects anything else, so there is no fifth case to render.
      return null;
    }
    if (part.kind === 'uploaded_file') {
      // OR-6125: the file the researcher attached — a compact, distinct
      // chip (NOT the full-width artifact/resource card) so they can see
      // what they uploaded and re-download the original. The download
      // streams from the auth'd source endpoint via a transient anchor.
      const file = part.uploadedFile;
      return (
        <Box
          key={index}
          data-testid={`uploaded-file-${file.uploadId}`}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            my: 1,
            paddingX: 1.5,
            paddingY: 0.75,
            maxWidth: '100%',
            border: (theme) => `1px solid ${theme.palette.divider}`,
            borderRadius: borderRadius.sm,
            backgroundColor: (theme) => theme.palette.background.paper,
          }}
        >
          <DescriptionOutlinedIcon fontSize="small" color="action" aria-hidden />
          <Typography
            variant="body2"
            title={file.fileName}
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {file.fileName}
          </Typography>
          <ButtonBase
            aria-label={`Download ${file.fileName}`}
            data-testid={`uploaded-file-download-${file.uploadId}`}
            onClick={() => {
              void downloadUploadSource(file.uploadId, file.fileName);
            }}
            sx={{
              display: 'inline-flex',
              padding: 0.5,
              borderRadius: borderRadius.sm,
              color: (theme) => theme.palette.primary.main,
              '&:hover': { backgroundColor: (theme) => theme.palette.action.hover },
            }}
          >
            <DownloadOutlinedIcon fontSize="small" />
          </ButtonBase>
        </Box>
      );
    }
    // ``attachment`` parts are rendered above the bubble, not in this switch —
    // see the user-turn branch below.
    if (part.kind === 'attachment') return null;
    if (part.kind === 'notice') {
      // Non-blocking inline notice (a backend WARNING, e.g. the stall
      // nudge). Theme-driven severity colours; not the terminal error.
      return (
        <Alert
          key={index}
          severity={part.severity}
          variant="outlined"
          data-testid="epi-chat-notice"
          // MUI gives .MuiAlert-message `overflow: auto`, so an unbroken token
          // (a dataset id, a slug) scrolls the notice sideways instead of wrapping.
          sx={{
            my: 1,
            '& .MuiAlert-message': {
              overflow: 'hidden',
              overflowWrap: 'anywhere',
              minWidth: 0,
            },
          }}
        >
          {part.message}
        </Alert>
      );
    }
    return null;
  });

  // User turn: initials avatar + light grey rounded bubble, left-aligned.
  // The bubble hugs its content (``width: fit-content``) rather than spanning
  // the column, matching the design. User turns are text-only in practice, so
  // ``content`` here is the rendered text part(s).
  if (isUser) {
    // Above the bubble: an attachment-only turn must not render an empty grey box.
    const attachments = message.parts.flatMap((part) =>
      part.kind === 'attachment' ? [part.attachment] : []
    );
    const hasText = message.parts.some((part) => part.kind === 'text' && part.text.trim());
    return (
      // 8px avatar→bubble gap (``spacing={2}``) — the USER ICON→PROMPT
      // ``gap-[8px]`` in the RA chat design (Figma 63:1877).
      <Stack direction="row" spacing={2} sx={{ width: '100%', alignItems: 'flex-start' }}>
        <Avatar
          // 24px secondary-cyan circle with 12px white initials — the USER ICON
          // (Figma 63:6367). No VDS token for avatar dimensions.
          sx={{
            width: 24,
            height: 24,
            fontSize: 12,
            fontWeight: 600,
            flexShrink: 0,
            bgcolor: 'secondary.main',
            color: 'common.white',
          }}
          data-testid="message-user-avatar"
        >
          {userInitials}
        </Avatar>
        <Stack spacing={1} sx={{ maxWidth: '100%', alignItems: 'flex-start', minWidth: 0 }}>
          {attachments.length > 0 ? (
            <Stack
              direction="row"
              spacing={1}
              data-testid="message-attachments"
              sx={{ flexWrap: 'wrap', rowGap: 1 }}
            >
              {attachments.map((attachment) => (
                <AttachmentCard
                  key={attachment.fileId}
                  filename={attachment.filename}
                  size={attachment.size}
                  testId={`attachment-${attachment.fileId}`}
                />
              ))}
            </Stack>
          ) : null}
          {hasText ? (
            <Box
              // Grey-200 (#eee) bubble, 12px/8px padding, 8px radius — the Prompts
              // bubble (Figma 63:6369). Hugs its content rather than spanning.
              sx={{
                maxWidth: '100%',
                width: 'fit-content',
                color: 'text.primary',
                backgroundColor: 'grey.200',
                borderRadius: 2,
                paddingX: 3,
                paddingY: 2,
              }}
            >
              {content}
            </Box>
          ) : null}
        </Stack>
      </Stack>
    );
  }

  // Agent (and system) turn: plain, full-width prose — no label, no avatar,
  // no bubble. The nested part rendering (tool groups, checkpoints, artifact /
  // resource cards, notices, reasoning) is unchanged.
  //
  // A multi-variable turn no longer needs render-time grouping — the
  // backend coalesces N create gates into ONE combined intent checkpoint
  // (member_count > 1), which renders through the single IntentApprovePrompt
  // below with count-aware copy.
  return <Box sx={{ width: '100%', color: 'text.primary' }}>{content}</Box>;
};

// ---------- Intent-checkpoint Approve prompt (OR-6290, AV-14) ----------

/** Approval message the researcher sends back to the agent. The cohort and
 *  concept-group strings mirror the analytical-variable one (the gateway
 *  stamp keys on ``choice``, not the text). */
/**
 * The Review card's reply is one numbered list with an entry per item, which can pass
 * nine. Markdown's default list padding fits a one-digit marker; "10." to "99." hang
 * further left and spill past the bubble's own padding. Sized in em so it follows the
 * bubble's font. TODO: cleanup with research_assistant_review_card ff
 */
const REVIEW_REPLY_MARKDOWN_SX: SxProps<Theme> = { '& ol': { paddingLeft: '2.25em' } };

const APPROVE_MESSAGES = {
  analytical_variable: 'Approved — build this variable.',
  cohort: 'Approved — run this cohort plan.',
  // OR-6271: concept-group create consent — a short yes to creating a NEW group.
  concept_group: 'Approved — create this concept group.',
} as const;

type ApprovePromptAssetKind = keyof typeof APPROVE_MESSAGES;

interface IntentApprovePromptProps {
  checkpoint: EpiCheckpoint;
  /** Which asset family the gate is about — selects the approval text and the
   *  review-copy wording (OR-6292 added ``cohort``). */
  assetKind: ApprovePromptAssetKind;
  onAnswer: (answer: EpiChatSendPayload) => void;
  /** Render the Approve button. True only while this checkpoint is the latest,
   *  unanswered message — once a newer message exists (approval or a typed
   *  redirect) the gate is superseded and the button is gone. */
  showApprove: boolean;
  /** Display name from the sibling asset resource card, when one rendered. */
  assetName: string | null;
  disabled?: boolean;
  /** OR-6504: opens a variable GROUP's details page from the family card. */
  onOpenVariableGroup?: (familyId: string) => void;
}

/**
 * Lean intent-checkpoint prompt for the asset-workflow flow (AV-14). The AV
 * asset card (a sibling ``resource_ref`` part) is the review surface (routing
 * to the asset page), and the agent's own check-in ``text`` part asks the
 * researcher to review + confirm. This component adds only the Approve
 * affordance.
 *
 * Approval is a CHAT message — it reuses the existing checkpoint-approval
 * mechanism (``{ text, checkpointId }``) the agent reads on its next turn, and
 * (OR-6290 Step 3) additionally carries the structured consent signal on the
 * existing ``choice`` wire field (``checkpoint_approve:<checkpointId>``) so the
 * gateway stamps a durable, deterministic approval for the gate — independent
 * of the model's interpretation of the reply text. Typing an approval instead
 * still works via the backend's surfaced-checkpoint fallback; there
 * is deliberately NO Edit affordance (to change the variable the researcher just
 * types into chat) and no asset-page approve action. ``draft_gating`` is
 * untouched. Never dead-ends: this renders from the always-present ``checkpoint``
 * part and reads nothing off the (best-effort) AV card, so the researcher can
 * always approve even if the card failed to render.
 */
interface VariableFamilyApprovePromptProps {
  checkpoint: EpiCheckpoint;
  family: EpiVariableFamily;
  onAnswer: IntentApprovePromptProps['onAnswer'];
  showApprove: boolean;
  disabled?: boolean;
  /** Opens the group's details page. Omitted ⇒ the card has no click-through. */
  onOpenVariableGroup?: (familyId: string) => void;
}

/**
 * OR-6504: the approval prompt for a parameterized variable family.
 *
 * One gate covers several variables that share a definition and differ in one
 * thing, so the researcher reviews ONE group: a line of prose, the group card,
 * the review directive, then a single Approve.
 *
 * This component owns only the decision — the copy, the gating, and what the
 * Approve click sends. What the researcher READS before deciding lives on the
 * group's details page, which the card opens; the chat stays three lines deep,
 * as it is for every other asset.
 */
const VariableFamilyApprovePrompt = ({
  checkpoint,
  family,
  onAnswer,
  showApprove,
  disabled,
  onOpenVariableGroup,
}: VariableFamilyApprovePromptProps) => {
  const intl = useIntl();
  // Deliberately the SAME string as the OR-6462 batch gate: one "Approve all"
  // label across both, so the affordance reads identically wherever it appears.
  // The accessible name below still spells out what is being built.
  const approveLabel = intl.formatMessage(
    {
      id: 'epiChat.intent.approveAllButton',
      defaultMessage: 'Approve all ({count})',
    },
    { count: family.memberCount }
  );
  return (
    <Box data-testid="variable-family-approve-prompt">
      <Typography variant="body2" data-testid="variable-family-review-prompt-copy">
        {intl.formatMessage(
          {
            id: 'epiChat.variableFamily.reviewPrompt',
            defaultMessage:
              "Rather than {count} separate drafts, I'll set them up as a single group — review the shared definition once, then I'll create all {count}:",
          },
          { count: family.memberCount }
        )}
      </Typography>
      <VariableFamilyGroupCard
        family={family}
        // No navigator ⇒ no click-through, rather than a chevron that goes
        // nowhere.
        onOpenDetails={onOpenVariableGroup ? () => onOpenVariableGroup(family.familyId) : undefined}
      />
      <Typography variant="body2" data-testid="variable-family-review-directive">
        {intl.formatMessage(
          {
            id: 'epiChat.variableFamily.reviewDirective',
            defaultMessage:
              'Review the shared definition, then approve to create all {count} variables at once.',
          },
          { count: family.memberCount }
        )}
      </Typography>
      {showApprove && (
        <Button
          variant="contained"
          color="primary"
          size="small"
          fullWidth
          sx={{ marginTop: 1.5 }}
          data-testid="variable-family-approve-button"
          disabled={disabled}
          // The visible label stays short so it survives a narrowed chat panel;
          // the accessible name spells the noun out, since "build all 10" read
          // on its own leaves "10 what?" unanswered.
          aria-label={intl.formatMessage(
            {
              id: 'epiChat.variableFamily.approveButtonAriaLabel',
              defaultMessage: 'Approve and build all {count} variables',
            },
            { count: family.memberCount }
          )}
          onClick={() =>
            onAnswer({
              text: intl.formatMessage(
                {
                  id: 'epiChat.variableFamily.approvedMessage',
                  defaultMessage: 'Approved — build all {count} variables from this definition.',
                },
                { count: family.memberCount }
              ),
              checkpointId: checkpoint.checkpointId,
              // Unchanged single-approve mechanism: the backend fans one
              // approval out across every member's gate ref.
              choice: `checkpoint_approve:${checkpoint.checkpointId}`,
            })
          }
        >
          {approveLabel}
        </Button>
      )}
    </Box>
  );
};

const IntentApprovePrompt = ({
  checkpoint,
  assetKind,
  onAnswer,
  showApprove,
  assetName,
  disabled,
  onOpenVariableGroup,
}: IntentApprovePromptProps) => {
  const intl = useIntl();
  const isCohort = assetKind === 'cohort';
  // OR-6271: the concept-group create-consent gate has no asset card to review —
  // the checkpoint summary IS the yes/no question ("no library group for X —
  // create a new one?"), so render it directly.
  const isConceptGroup = assetKind === 'concept_group';
  // OR-6368: the envelope says whether this gate follows a create or an edit
  // (`request_approval` re-surfaces stamp "updated"), so the review line stops
  // claiming "I've created …" for updates. Absent (legacy/replay) reads as
  // created — byte-identical copy to before.
  const isUpdate = checkpoint.assetOperation === 'updated';
  // A combined "Approve all" gate — the backend coalesced N create gates
  // surfaced in one turn into this single checkpoint (assetCount > 1). The review
  // line uses count-based copy and one Approve stamps every member.
  const batchCount =
    assetKind === 'analytical_variable' || assetKind === 'cohort' ? checkpoint.assetCount ?? 1 : 1;
  const isBatch = batchCount > 1;
  // OR-6504: a parameterized variable FAMILY. Distinct from a batch, not a
  // variant of it: only ONE draft exists here, so the batch line ("I've created
  // N …") would be false in both count and tense. The backend already suppresses
  // assetCount when this block is present; this branch owns the rendering.
  const family = assetKind === 'analytical_variable' ? checkpoint.family : undefined;
  if (family) {
    return (
      <VariableFamilyApprovePrompt
        checkpoint={checkpoint}
        family={family}
        onAnswer={onAnswer}
        showApprove={showApprove}
        disabled={disabled}
        onOpenVariableGroup={onOpenVariableGroup}
      />
    );
  }
  return (
    <Box data-testid="intent-approve-prompt">
      {
        // OR-6336: the review sentence renders unconditionally — the agent's
        // own prose cannot be relied on to carry the review-and-approve ask
        // (the gate cuts its turn right after the ``checkpoint`` tool call).
        // Unlike the button, the copy persists after answering — it reads as
        // the agent's check-in line in the transcript.
      }
      <Typography
        variant="body2"
        data-testid="intent-review-prompt-copy"
        sx={{ marginBottom: showApprove ? 1.5 : 0 }}
      >
        {isConceptGroup
          ? checkpoint.summary
          : isCohort
          ? isBatch
            ? intl.formatMessage(
                {
                  id: 'epiChat.cohortPlan.approveAllReviewPrompt',
                  defaultMessage:
                    "I've drafted {count} cohorts. Review their criteria on the cohort pages, then let me know if you'd like to approve them or make changes.",
                },
                { count: batchCount }
              )
            : assetName
            ? intl.formatMessage(
                isUpdate
                  ? {
                      id: 'epiChat.cohortPlan.updateReviewPrompt',
                      defaultMessage:
                        "I've updated the cohort {name}. Review its criteria on the cohort page, then let me know if you'd like to approve the plan or make further changes.",
                    }
                  : {
                      id: 'epiChat.cohortPlan.reviewPrompt',
                      defaultMessage:
                        "I've drafted the cohort {name}. Review its criteria on the cohort page, then let me know if you'd like to approve the plan or make changes.",
                    },
                { name: assetName }
              )
            : intl.formatMessage(
                isUpdate
                  ? {
                      id: 'epiChat.cohortPlan.updateReviewPromptGeneric',
                      defaultMessage:
                        "I've updated the cohort plan. Review its criteria on the cohort page, then let me know if you'd like to approve the plan or make further changes.",
                    }
                  : {
                      id: 'epiChat.cohortPlan.reviewPromptGeneric',
                      defaultMessage:
                        "I've drafted the cohort plan. Review its criteria on the cohort page, then let me know if you'd like to approve the plan or make changes.",
                    }
              )
          : isBatch
          ? intl.formatMessage(
              isUpdate
                ? {
                    id: 'epiChat.intent.approveAllUpdatedReviewPrompt',
                    defaultMessage:
                      "I've updated {count} variables. Review them in the workspace, then let me know if you'd like to approve them or make changes.",
                  }
                : checkpoint.assetOperation === 'mixed'
                ? {
                    id: 'epiChat.intent.approveAllMixedReviewPrompt',
                    defaultMessage:
                      "I've created or updated {count} variables. Review them in the workspace, then let me know if you'd like to approve them or make changes.",
                  }
                : {
                    id: 'epiChat.intent.approveAllReviewPrompt',
                    defaultMessage:
                      "I've created {count} variables. Review them in the workspace, then let me know if you'd like to approve them or make changes.",
                  },
              { count: batchCount }
            )
          : assetName
          ? intl.formatMessage(
              isUpdate
                ? {
                    id: 'epiChat.intent.updateReviewPrompt',
                    defaultMessage:
                      "I've updated the variable {name}. Review the changes in the workspace, then let me know if you'd like to approve them or make further changes.",
                  }
                : {
                    id: 'epiChat.intent.reviewPrompt',
                    defaultMessage:
                      "I've created the variable {name}. Review the definition in the workspace, then let me know if you'd like to approve it or make changes.",
                  },
              { name: assetName }
            )
          : intl.formatMessage(
              isUpdate
                ? {
                    id: 'epiChat.intent.updateReviewPromptGeneric',
                    defaultMessage:
                      "I've updated the variable draft. Review the changes in the workspace, then let me know if you'd like to approve them or make further changes.",
                  }
                : {
                    id: 'epiChat.intent.reviewPromptGeneric',
                    defaultMessage:
                      "I've created the variable draft. Review the definition in the workspace, then let me know if you'd like to approve it or make changes.",
                  }
            )}
      </Typography>
      {
        // OR-6448: platform-composed disclosures (e.g. "the requirement X is
        // already enforced by the index date" when the plan check removed an
        // entailed criterion). Deterministic — the model's prose demonstrably
        // does not carry these, and the review line above is fixed i18n copy.
        // Persist after answering, like the review line: the transcript must
        // keep showing WHY a stated requirement has no criterion of its own.
        // TODO: cleanup with research_assistant_criterion_redundancy_guard ff
      }
      {(checkpoint.summaryNotes ?? []).map((note, noteIndex) => (
        <Typography
          key={noteIndex}
          variant="body2"
          data-testid="intent-approve-summary-note"
          sx={{ marginBottom: showApprove ? 1.5 : 0, fontStyle: 'italic' }}
        >
          {note}
        </Typography>
      ))}
      {showApprove && (
        <Button
          variant="contained"
          color="primary"
          size="small"
          data-testid="intent-approve-button"
          disabled={disabled}
          onClick={() =>
            onAnswer({
              // Plural approval message for a combined multi-asset gate.
              text: isBatch
                ? isCohort
                  ? 'Approved — run all these cohort plans.'
                  : 'Approved — build all these variables.'
                : APPROVE_MESSAGES[assetKind],
              checkpointId: checkpoint.checkpointId,
              // OR-6290 Step 3: structured consent — the gateway recognizes this
              // prefix and stamps the durable approval for the checkpoint's gate.
              // For a combined gate the backend stamps one approval per member.
              choice: `checkpoint_approve:${checkpoint.checkpointId}`,
            })
          }
        >
          {isBatch
            ? // A combined multi-variable gate approves every member at
              // once, so the button says so with the count.
              intl.formatMessage(
                {
                  id: 'epiChat.intent.approveAllButton',
                  defaultMessage: 'Approve all ({count})',
                },
                { count: batchCount }
              )
            : intl.formatMessage({
                id: 'epiChat.intent.approveButton',
                defaultMessage: 'Approve',
              })}
        </Button>
      )}
    </Box>
  );
};

// ---------- Inline card layout (shared by artifact_ref and resource_ref) ----------

// ── Behavior hooks: all the asset-status side effects (query/poll) live here, so
// the card components below are presentational and the data flow is explicit.
// Each returns the backend ``assetStatus`` label to render, defaulting to Draft
// (the backend mapper's own safe default) while the asset is still loading.

/**
 * Cohort status. Reads the backend ``assetStatus`` off the live
 * cohort-profile query and drives a **self-limiting 5s poll while Processing**
 * so the pill flips to Ready/Error live even when the user only watches chat —
 * cohort processing finishes in an async worker (no agent turn), so a poll is
 * the only thing that catches completion. The poll signal is held in state
 * because it depends on the query's own result (the effect flips it on at
 * Processing, off at a terminal status).
 */
const useCohortStatusLabel = (ref: EpiResourceRef): string => {
  const cohortId = ref.resourceId ?? '';
  const [shouldPoll, setShouldPoll] = useState(false);
  const { data: cohortProfile } = useCohortProfileQuery({
    projectId: ref.projectId,
    cohortId,
    enabled: !!cohortId,
    shouldPoll,
  });
  const isProcessing = cohortProfile?.assetStatus === ASSET_STATUS.PROCESSING;
  useEffect(() => {
    setShouldPoll(isProcessing);
  }, [isProcessing]);
  return cohortProfile?.assetStatus ?? ASSET_STATUS.DRAFT;
};

/**
 * Analytical-variable status. Reads the backend ``assetStatus``
 * off the chat-aware ``/by-ref`` query (keyed by the card's ``av.<slug>`` +
 * ``chatId``), which resolves the in-flight draft too. **No poll:** an AV's
 * verification (Draft → Ready) completes *in-turn* — the agent is active when it
 * finishes, and a save-time invalidation / window-focus refetch catches it — so
 * unlike cohort there's no async-worker completion to poll for. (AV has no
 * Processing asset-status; Error is deferred to Draft.)
 */
const useAvStatusLabel = (ref: EpiResourceRef, chatId: string | null | undefined): string => {
  const { data } = useVariableDetailByRef(ref.resourceId ?? null, ref.projectId, chatId);
  return data?.data?.assetStatus ?? ASSET_STATUS.DRAFT;
};

interface ResourceStatusCardProps {
  resourceRef: EpiResourceRef;
  statusLabel: string;
  onNavigateResource?: (route: string) => void;
}

/**
 * Presentational inline resource card for the asset-status path —
 * the object-cards spec (Figma 1491-64018) via {@link AssetCard}. Renders the
 * derived ``statusLabel`` in the chip, EXCEPT when the pushed build-progress
 * ``ref.status`` is ``error``: a failed build has nothing committed
 * server-side, so the asset-status query would keep reading the stale
 * (usually Draft) row — the card-level error wins the chip. Disabled/error
 * click gating stays on ``ref.status``. No side effects — the status comes
 * from a behavior hook in the wrapper.
 */
const ResourceStatusCard = ({
  resourceRef: ref,
  statusLabel,
  onNavigateResource,
}: ResourceStatusCardProps) => {
  const isDisabled = ref.status === 'error' || !onNavigateResource;
  return (
    <AssetCard
      testId={`resource-ref-${ref.resourceId ?? ref.clientId}`}
      ariaLabel={`Open ${ref.name}`}
      kind={ref.resourceKind}
      name={ref.name}
      statusLabel={ref.status === 'error' ? ASSET_STATUS.ERROR : statusLabel}
      createdAt={ref.createdAt}
      disabled={isDisabled}
      onClick={() => {
        if (isDisabled) return;
        onNavigateResource?.(resourceCardRoute(ref));
      }}
    />
  );
};

interface CohortResourceRefCardProps {
  resourceRef: EpiResourceRef;
  onNavigateResource?: (route: string) => void;
}

/** Cohort card: behavior hook + presentational render. */
const CohortResourceRefCard = ({ resourceRef, onNavigateResource }: CohortResourceRefCardProps) => {
  const statusLabel = useCohortStatusLabel(resourceRef);
  return (
    <ResourceStatusCard
      resourceRef={resourceRef}
      statusLabel={statusLabel}
      onNavigateResource={onNavigateResource}
    />
  );
};

interface AvResourceRefCardProps {
  resourceRef: EpiResourceRef;
  onNavigateResource?: (route: string) => void;
  chatId?: string | null;
}

/** Analytical-variable card: behavior hook + presentational render. */
const AvResourceRefCard = ({ resourceRef, onNavigateResource, chatId }: AvResourceRefCardProps) => {
  const statusLabel = useAvStatusLabel(resourceRef, chatId);
  return (
    <ResourceStatusCard
      resourceRef={resourceRef}
      statusLabel={statusLabel}
      onNavigateResource={onNavigateResource}
    />
  );
};

interface ConceptGroupResourceRefCardProps {
  resourceRef: EpiResourceRef;
  onNavigateResource?: (route: string) => void;
  /** See `useConceptGroupStatusLabel`'s `defer` param (OR-6858). */
  deferStatusFetch?: boolean;
}

/** Concept-group card: behavior hook + presentational render. */
const ConceptGroupResourceRefCard = ({
  resourceRef,
  onNavigateResource,
  deferStatusFetch,
}: ConceptGroupResourceRefCardProps) => {
  const statusLabel = useConceptGroupStatusLabel(resourceRef, deferStatusFetch);
  return (
    <ResourceStatusCard
      resourceRef={resourceRef}
      statusLabel={statusLabel}
      onNavigateResource={onNavigateResource}
    />
  );
};

interface AnalysisResourceRefCardProps {
  resourceRef: EpiResourceRef;
  onNavigateResource?: (route: string) => void;
}

/** Analysis card: behavior hook + presentational render. */
const AnalysisResourceRefCard = ({
  resourceRef,
  onNavigateResource,
}: AnalysisResourceRefCardProps) => {
  const statusLabel = useAnalysisStatusLabel(resourceRef);
  return (
    <ResourceStatusCard
      resourceRef={resourceRef}
      statusLabel={statusLabel}
      onNavigateResource={onNavigateResource}
    />
  );
};

interface ResourceCardLayoutProps {
  testId: string;
  ariaLabel: string;
  kindLabel: string;
  name: string;
  /** Sub-line: slug + timestamp (artifact_ref) or status + timestamp (resource_ref). */
  metaText: string;
  ctaLabel: string;
  disabled?: boolean;
  onClick: () => void;
}

/**
 * Full-width primary-tinted card the chat stream uses to point the user
 * at an artifact (drawer) or a project resource (route). Designed to be
 * unmissable: inset from the prose width, generous vertical margin, an
 * explicit CTA on the right edge.
 *
 * Shared layout — diverges from the inline original only in that the
 * trailing CTA label is now caller-supplied (Open / Drafting… / View /
 * Failed) so the same shell handles both states without conditionals
 * leaking into the call sites.
 */
const ResourceCardLayout = ({
  testId,
  ariaLabel,
  kindLabel,
  name,
  metaText,
  ctaLabel,
  disabled = false,
  onClick,
}: ResourceCardLayoutProps) => {
  return (
    <Box sx={{ marginY: 2.5, marginX: { xs: 0, sm: 2 } }}>
      <ButtonBase
        onClick={onClick}
        disabled={disabled}
        data-testid={testId}
        aria-label={ariaLabel}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          width: '100%',
          gap: 1.5,
          paddingX: 2,
          paddingY: 1.5,
          borderRadius: 1.5,
          textAlign: 'left',
          border: (theme) =>
            disabled
              ? `1px dashed ${theme.palette.divider}`
              : `1px solid ${theme.palette.primary.light}`,
          backgroundColor: (theme) =>
            disabled
              ? theme.palette.action.hover
              : theme.palette.mode === 'dark'
              ? `${theme.palette.primary.dark}20`
              : `${theme.palette.primary.light}1f`,
          boxShadow: (theme) =>
            disabled
              ? 'none'
              : theme.palette.mode === 'dark'
              ? `0 1px 2px ${theme.palette.common.black}40`
              : `0 1px 2px ${theme.palette.primary.light}40`,
          transition: 'background-color 0.15s, border-color 0.15s, box-shadow 0.15s',
          '&:hover': disabled
            ? undefined
            : {
                backgroundColor: (theme) =>
                  theme.palette.mode === 'dark'
                    ? `${theme.palette.primary.dark}40`
                    : `${theme.palette.primary.light}33`,
                borderColor: (theme) => theme.palette.primary.main,
              },
          '&:focus-visible': {
            outline: (theme) => `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
          },
        }}
      >
        <DescriptionOutlinedIcon
          sx={{
            color: disabled ? 'text.disabled' : 'primary.main',
            fontSize: 22,
            flexShrink: 0,
            marginTop: 0.25,
          }}
        />
        <Stack sx={{ flex: 1, minWidth: 0, gap: 0.25 }}>
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              letterSpacing: '0.08em',
              color: disabled ? 'text.secondary' : 'primary.main',
              lineHeight: 1.2,
            }}
          >
            {kindLabel}
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              minWidth: 0,
              lineHeight: 1.3,
            }}
            noWrap
          >
            {name}
          </Typography>
          {metaText && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ minWidth: 0, marginTop: 0.25 }}
              noWrap
            >
              {metaText}
            </Typography>
          )}
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{
            flexShrink: 0,
            color: disabled ? 'text.secondary' : 'primary.main',
            alignSelf: 'center',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
            }}
          >
            {ctaLabel}
          </Typography>
          {!disabled && <ArrowForwardRoundedIcon sx={{ fontSize: 18 }} />}
        </Stack>
      </ButtonBase>
    </Box>
  );
};

// ---------- Helpers for the two ref shapes ----------

const formatCreatedAt = (iso: string): string => {
  const t = new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return `created ${t}`;
};

const metaLine = (parts: ReadonlyArray<string | undefined>): string => {
  return parts.filter((p): p is string => Boolean(p && p.length > 0)).join(' · ');
};
